import { APP_PATHS, FLOW_STEP_PATHS } from '@/constants';
import { VAT_ID_OPTIONS, REGISTRATION_ID_OPTIONS } from '@/constants/countries';
import ApplicationConfirmation from '@/containers/ApplicationConfirmation';
import BankAccounts from '@/containers/BankAccounts';
import CompanyRegistrationId from '@/containers/CompanyRegistrationId';
import Documents from '@/containers/Documents';
import EntityTypeSelection from '@/containers/EntityTypeSelection';
import StoreDetails from '@/containers/StoreDetails';
import ProductSelection from '@/containers/ProductSelection';
import VatId from '@/containers/VatId';

import { ReviewBlock } from '@/flow/blocks/kyc/common/Review.block';
import { not } from '@/flow/common';

import {
  canSubmitApplication,
  getDefaultRoleConstraints,
  hasBankAccounts,
  hasRegistrationInformation,
  hasSelectedProduct,
  hasStoreDetails,
  hasVatInformation,
  isMerchantRegistered,
  isMerchantTransactionsEnabled,
  isMerchantTransactionsReviewAutomated,
  isMerchantTransactionsReviewAutomatedOrManual,
  shouldCollectDocuments,
} from '../selectors';

import {
  getNextAfterStepOne,
  getNextApplicationConfirmation,
  globalPrerequisites,
  prerequisitesForStoreDetails,
} from './common';

export default ({
  isManagedFlow,
  isUnmanagedKyc,
  StakeholderBlock,
  isWidget,
  countryConfig,
  merchant,
}) => {
  const stakeholderBlock = new StakeholderBlock({
    isManagedFlow,
    merchantCountry: merchant.country,
    countryConfig,
    prerequisite:
      countryConfig.VAT_ID_KIND !== VAT_ID_OPTIONS.UNAVAILABLE && !countryConfig.ENRICHES_VAT
        ? ['hasVatInformation']
        : [],
  });
  const reviewBlock = new ReviewBlock({ isManagedFlow, isUnmanagedKyc, isWidget });

  return {
    // This facilitates when looking into the snapshots
    merchantCountry: merchant.country,
    stepNotFoundPath: FLOW_STEP_PATHS.APPLICATION_CONFIRMATION_PATH,
    globalPrerequisites: globalPrerequisites({ isUnmanagedKyc }),
    selectors: {
      canSubmitApplication: canSubmitApplication(merchant.country),
      defaultRoleConstraints: getDefaultRoleConstraints,
      hasBankAccounts,
      hasRegistrationInformation,
      hasSelectedProduct,
      hasStoreDetails,
      hasVatInformation,
      isMerchantRegistered,
      isMerchantTransactionsEnabled,
      isMerchantTransactionsReviewAutomated,
      isMerchantTransactionsReviewAutomatedOrManual,
      shouldCollectDocuments,
      ...stakeholderBlock.getSelectors(),
      ...reviewBlock.getSelectors(),
    },
    neededCaps: {},
    steps: [
      {
        id: 'product-selection',
        path: FLOW_STEP_PATHS.PRODUCT_SELECTION_PATH,
        Container: ProductSelection,
        back: false,
        next: FLOW_STEP_PATHS.STORE_DETAILS_PATH,
        exclude: !isUnmanagedKyc,
      },
      /* GENERAL STEPS */
      {
        id: 'store-details',
        path: FLOW_STEP_PATHS.STORE_DETAILS_PATH,
        ...prerequisitesForStoreDetails({ isUnmanagedKyc }),
        Container: StoreDetails,
        back: [
          {
            condition: isUnmanagedKyc,
            path: APP_PATHS.PRODUCT_SELECTION_PATH,
          },
        ],
        next: [
          {
            condition: countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
          },
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
              countryConfig.VAT_ID_KIND !== VAT_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.VAT_ID_PATH,
          },
          {
            condition: true,
            path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
          },
        ],
      },
      {
        id: 'company-registration-id',
        exclude: countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE,
        path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
        prerequisite: 'hasStoreDetails',
        Container: CompanyRegistrationId,
        back: FLOW_STEP_PATHS.STORE_DETAILS_PATH,
        next: [
          {
            condition:
              // If the VAT ID should be collected and;
              // the VAT ID is not enriched or should be enriched but there is no registration ID,
              // we should render the VAT ID step
              countryConfig.VAT_ID_KIND !== VAT_ID_OPTIONS.UNAVAILABLE &&
              (!countryConfig.ENRICHES_VAT || not('isMerchantRegistered')),
            path: FLOW_STEP_PATHS.VAT_ID_PATH,
          },
          {
            condition:
              isManagedFlow &&
              countryConfig.ALLOW_PUBLIC_ON_MANAGED &&
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.TIN,
            path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
          },
          ...stakeholderBlock.generateNextForPreviousStep(),
        ],
      },
      {
        id: 'vat-id',
        path: FLOW_STEP_PATHS.VAT_ID_PATH,
        prerequisite: 'hasRegistrationInformation',
        Container: VatId,
        back: [
          {
            condition: countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.STORE_DETAILS_PATH,
          },
          {
            condition: true,
            path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
          },
        ],
        next: [
          {
            condition:
              (countryConfig.ALLOW_PUBLIC_ON_MANAGED && isManagedFlow) ||
              (countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
                countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE),
            path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
          },
          {
            condition:
              // TIN collects entity type on the TIN step
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.TIN &&
              // If they are always registered, this will never be reached
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.FORCED &&
              not('isMerchantRegistered'),
            path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
          },
          ...stakeholderBlock.generateNextForPreviousStep(),
        ],
        exclude: countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE,
      },
      /* REGISTERED STEPS */
      {
        id: 'entity-type-selection',
        exclude:
          // TIN collects entity type on the TIN step
          countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.TIN ||
          // IF the country is only registered and you never get to choose public, we can exclude
          (!isManagedFlow &&
            countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.FORCED &&
            countryConfig.ALLOW_PUBLIC_ON_UNMANAGED === false) ||
          // Unless it's managed flow and you can collect public there
          (isManagedFlow &&
            !countryConfig.ALLOW_PUBLIC_ON_MANAGED &&
            countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.FORCED),
        path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
        prerequisite: [
          // Has no registration or VAT steps
          (countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
            countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE &&
            'hasStoreDetails') ||
            // Has registration but no VAT
            (countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE &&
              'hasRegistrationInformation') ||
            // Has both but enriches VAT (reg num should be there)
            (countryConfig.ENRICHES_VAT && 'hasRegistrationInformation') ||
            // Has both and does not enrich
            'hasVatInformation',
          ...(!countryConfig.ALLOW_PUBLIC_ON_UNMANAGED ? [not('isMerchantRegistered')] : []),
        ],
        Container: EntityTypeSelection,
        back: [
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
              countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.STORE_DETAILS_PATH,
          },
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
              countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
          },
          {
            condition: !countryConfig.ENRICHES_VAT || not('isMerchantRegistered'),
            path: FLOW_STEP_PATHS.VAT_ID_PATH,
          },
          {
            condition: true,
            path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
          },
        ],
        next: stakeholderBlock.generateNextForPreviousStep(),
      },
      ...stakeholderBlock.generateFlowConfig({
        back: [
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.TIN &&
              ((countryConfig.ALLOW_PUBLIC_ON_MANAGED && isManagedFlow) ||
                (merchant.country !== 'AU' && countryConfig.ALLOW_PUBLIC_ON_UNMANAGED)),
            path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
          },
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.TIN &&
              // If they are always registered, this will never be reached
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.FORCED &&
              not('isMerchantRegistered'),
            path: FLOW_STEP_PATHS.ENTITY_TYPE_SELECTION_PATH,
          },
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND === REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
              countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.STORE_DETAILS_PATH,
          },
          {
            condition:
              countryConfig.REGISTRATION_ID_KIND !== REGISTRATION_ID_OPTIONS.UNAVAILABLE &&
              countryConfig.VAT_ID_KIND === VAT_ID_OPTIONS.UNAVAILABLE,
            path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
          },
          {
            condition: !countryConfig.ENRICHES_VAT || not('isMerchantRegistered'),
            path: FLOW_STEP_PATHS.VAT_ID_PATH,
          },
          {
            condition: true,
            path: FLOW_STEP_PATHS.COMPANY_REGISTRATION_PATH,
          },
        ],
        next: getNextAfterStepOne({ isManagedFlow }),
      }),
      {
        id: 'bank-accounts',
        path: FLOW_STEP_PATHS.BANK_ACCOUNT_PATH,
        prerequisite: stakeholderBlock.getSelectorToSubmitStep(),
        skip: isManagedFlow,
        back: stakeholderBlock.generateBackForNextStep(),
        next: [
          { condition: 'shouldCollectDocuments', path: FLOW_STEP_PATHS.DOCUMENTS },
          { condition: true, path: APP_PATHS.APPLICATION_CONFIRMATION_PATH },
        ],
        Container: BankAccounts,
        extraProps: {
          // If this is the default flow we do not want to require that they
          // provide an account in merchant country currency
          ensureAccountInMerchantCurrency: !countryConfig.isDefault,
        },
      },
      {
        id: 'documents',
        path: FLOW_STEP_PATHS.DOCUMENTS,
        prerequisite: isManagedFlow
          ? 'shouldCollectDocuments'
          : ['shouldCollectDocuments', 'hasBankAccounts'],
        Container: Documents,
        skip: not('shouldCollectDocuments'),
        back: FLOW_STEP_PATHS.BANK_ACCOUNT_PATH,
        next: APP_PATHS.APPLICATION_CONFIRMATION_PATH,
      },
      {
        id: 'application-confirmation',
        path: FLOW_STEP_PATHS.APPLICATION_CONFIRMATION_PATH,
        prerequisite: ['canSubmitApplication', stakeholderBlock.getSelectorToSubmitStep()],
        Container: ApplicationConfirmation,
        back: [
          { condition: 'shouldCollectDocuments', path: FLOW_STEP_PATHS.DOCUMENTS },
          { condition: true, path: FLOW_STEP_PATHS.BANK_ACCOUNT_PATH },
        ],
        next: getNextApplicationConfirmation({ isUnmanagedKyc }),
      },
      ...reviewBlock.generateFlowConfig(),
    ],
  };
};
